// src/hooks/useOrganigrammeParams.js
import { useCallback, useEffect, useState } from "react";
import { getAuthHeaders } from "../services/api";

const ORGANIGRAMME_EVENT = "organigrammeParamsUpdated";

export const triggerOrganigrammeUpdate = () => {
  window.dispatchEvent(new CustomEvent(ORGANIGRAMME_EVENT));
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || `Erreur ${response.status}`);
  }

  return data;
};

const parseFileResponse = async (response) => {
  if (response.ok) return response;

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || `Erreur ${response.status}`);
  }

  const text = await response.text().catch(() => "");
  throw new Error(text || `Erreur ${response.status}`);
};

const getJsonHeaders = () => getAuthHeaders();

const getFormDataHeaders = () => {
  const headers = getAuthHeaders();
  delete headers["Content-Type"];
  return headers;
};

const getFilenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const classicMatch = disposition.match(/filename="?([^"]+)"?/i);
  const rawFilename = utf8Match?.[1] || classicMatch?.[1];

  if (!rawFilename) return fallback;

  try {
    return decodeURIComponent(rawFilename);
  } catch {
    return rawFilename;
  }
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const useOrganigrammeParams = () => {
  const [organigramme, setOrganigramme] = useState([]);
  const [organigrammeByLevel, setOrganigrammeByLevel] = useState({});
  const [reglementActif, setReglementActif] = useState(null);
  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReglement, setLoadingReglement] = useState(true);
  const [error, setError] = useState(null);
  const [reglementError, setReglementError] = useState(null);

  const loadOrganigramme = useCallback(async ({ actif } = {}) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (actif) params.set("actif", "1");

      const url = `/api/parametres/organigramme${params.toString() ? `?${params}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      const data = await parseResponse(response);

      setOrganigramme(data.data?.organigramme || []);
      setOrganigrammeByLevel(data.data?.organigrammeByLevel || {});
      setError(null);

      return data.data;
    } catch (err) {
      setError(err.message);
      setOrganigramme([]);
      setOrganigrammeByLevel({});
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const addOrganigrammeItem = async (payload) => {
    try {
      const response = await fetch("/api/parametres/organigramme", {
        method: "POST",
        headers: getJsonHeaders(),
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await parseResponse(response);

      await loadOrganigramme();
      triggerOrganigrammeUpdate();

      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateOrganigrammeItem = async (id, payload) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/${id}`, {
        method: "PUT",
        headers: getJsonHeaders(),
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await parseResponse(response);

      await loadOrganigramme();
      triggerOrganigrammeUpdate();

      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteOrganigrammeItem = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/${id}`, {
        method: "DELETE",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseResponse(response);

      await loadOrganigramme();
      triggerOrganigrammeUpdate();

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const loadReglement = useCallback(async () => {
    try {
      setLoadingReglement(true);

      const response = await fetch("/api/parametres/organigramme/reglement", {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      const data = await parseResponse(response);

      setReglementActif(data.data?.actif || null);
      setReglements(data.data?.documents || []);
      setReglementError(null);

      return data.data;
    } catch (err) {
      setReglementError(err.message);
      setReglementActif(null);
      setReglements([]);
      return null;
    } finally {
      setLoadingReglement(false);
    }
  }, []);

  const uploadReglement = async ({ fichier, titre, description, version, contenu }) => {
    try {
      const formData = new FormData();

      formData.append("titre", titre || "Reglement interieur");

      if (description) formData.append("description", description);
      if (version) formData.append("version", version);
      if (contenu) formData.append("contenu", contenu);
      if (fichier) formData.append("fichier", fichier);

      const response = await fetch("/api/parametres/organigramme/reglement", {
        method: "POST",
        headers: getFormDataHeaders(),
        credentials: "include",
        body: formData,
      });

      const data = await parseResponse(response);

      await loadReglement();
      triggerOrganigrammeUpdate();

      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const activateReglement = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/reglement/${id}/activer`, {
        method: "PUT",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseResponse(response);

      await loadReglement();
      triggerOrganigrammeUpdate();

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteReglement = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/reglement/${id}`, {
        method: "DELETE",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseResponse(response);

      await loadReglement();
      triggerOrganigrammeUpdate();

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };



  const downloadOrganigrammePdf = async () => {
    try {
      const response = await fetch("/api/parametres/organigramme/pdf", {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseFileResponse(response);

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("content-disposition"),
        "organigramme.pdf"
      );

      downloadBlob(blob, filename);

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };


  const downloadReglementPdf = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/reglement/${id}/pdf`, {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseFileResponse(response);

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("content-disposition"),
        "reglement-interieur.pdf"
      );

      downloadBlob(blob, filename);

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };


  const downloadReglementFile = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/reglement/${id}/fichier/download`, {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseFileResponse(response);

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("content-disposition"),
        "reglement-interieur"
      );

      downloadBlob(blob, filename);

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const fetchReglementFilePreview = async (id) => {
    try {
      const response = await fetch(`/api/parametres/organigramme/reglement/${id}/fichier/view`, {
        method: "GET",
        headers: getJsonHeaders(),
        credentials: "include",
      });

      await parseFileResponse(response);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const filename = getFilenameFromDisposition(
        response.headers.get("content-disposition"),
        "reglement-interieur"
      );

      return {
        success: true,
        url,
        blob,
        filename,
        mimeType: blob.type || response.headers.get("content-type") || "",
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    loadOrganigramme();
    loadReglement();

    const handleUpdate = () => {
      loadOrganigramme();
      loadReglement();
    };

    window.addEventListener(ORGANIGRAMME_EVENT, handleUpdate);

    return () => {
      window.removeEventListener(ORGANIGRAMME_EVENT, handleUpdate);
    };
  }, [loadOrganigramme, loadReglement]);

  return {
    organigramme,
    organigrammeByLevel,
    reglementActif,
    reglements,
    loading,
    loadingReglement,
    error,
    reglementError,
    loadOrganigramme,
    addOrganigrammeItem,
    updateOrganigrammeItem,
    deleteOrganigrammeItem,
    loadReglement,
    uploadReglement,
    activateReglement,
    deleteReglement,
    downloadReglementPdf,
    downloadOrganigrammePdf,
    downloadReglementFile,
    fetchReglementFilePreview,
  };
};

export default useOrganigrammeParams;